import React, { useState } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaPlay, FaUpload } from 'react-icons/fa';
import { SERVER } from '../config/config';

const VideoManager = ({ 
  videos = [], 
  onAdd, 
  onUpdate, 
  onDelete, 
  canEdit = true, 
  canCreate = true, 
  canDelete = true 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    video_title: '',
    video_file: null,
    order_index: 0
  });

  const resetForm = () => {
    setFormData({
      video_title: '',
      video_file: null,
      order_index: 0
    });
    setEditingVideo(null);
  };

  const openModal = (video = null) => {
    if (video) {
      setFormData({
        video_title: video.video_title || '',
        video_file: null, // Don't pre-fill file input
        order_index: video.order_index || 0
      });
      setEditingVideo(video);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.video_title.trim()) {
      alert('Video title is required');
      return;
    }

    if (!editingVideo && !formData.video_file) {
      alert('Video file is required');
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('video_title', formData.video_title);
      formDataToSend.append('order_index', formData.order_index);
      
      if (formData.video_file) {
        formDataToSend.append('video', formData.video_file);
      }

      if (editingVideo) {
        await onUpdate(editingVideo.id, formDataToSend);
      } else {
        await onAdd(formDataToSend);
      }
      closeModal();
    } catch (error) {
      console.error('Error saving video:', error);
      alert('Error saving video. Please try again.');
    }
  };

  const handleDelete = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await onDelete(videoId);
      } catch (error) {
        console.error('Error deleting video:', error);
        alert('Error deleting video. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Videos ({videos.length})</h3>
        {canCreate && (
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaPlus size={14} />
            Add Video
          </button>
        )}
      </div>

      {/* Videos List */}
      {videos.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((video) => (
            <div key={video.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
              {/* Video Thumbnail */}
              <div className="relative aspect-video bg-gray-100">
                <video
                  src={`${SERVER}${video.video_file}`}
                  className="w-full h-full object-cover"
                  muted
                  preload="metadata"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                
                {/* Fallback for video load error */}
                <div className="absolute inset-0 bg-gray-200 flex items-center justify-center" style={{ display: 'none' }}>
                  <FaPlay className="text-gray-400" size={24} />
                </div>
                
                {/* Play button overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                  <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                    <FaPlay className="text-gray-800 ml-1" size={16} />
                  </div>
                </div>
              </div>

              {/* Video Info */}
              <div className="p-4">
                <h4 className="font-medium text-gray-900 mb-2 line-clamp-2">
                  {video.video_title || 'Untitled Video'}
                </h4>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    Order: {video.order_index}
                  </span>

                  <div className="flex items-center gap-2">
                    {canEdit && (
                      <button
                        onClick={() => openModal(video)}
                        className="text-gray-600 hover:text-blue-600 transition-colors"
                        title="Edit video"
                      >
                        <FaEdit size={14} />
                      </button>
                    )}
                    
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(video.id)}
                        className="text-gray-600 hover:text-red-600 transition-colors"
                        title="Delete video"
                      >
                        <FaTrash size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <FaPlay size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No videos added yet</p>
          {canCreate && (
            <button
              onClick={() => openModal()}
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              Add your first video
            </button>
          )}
        </div>
      )}

      {/* Video Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingVideo ? 'Edit Video' : 'Add Video'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Video Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video Title *
                  </label>
                  <input
                    type="text"
                    value={formData.video_title}
                    onChange={(e) => setFormData(prev => ({ ...prev, video_title: e.target.value }))}
                    placeholder="Enter video title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                {/* Video File */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video File {!editingVideo && '*'}
                  </label>
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <FaUpload className="w-8 h-8 mb-4 text-gray-500" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">Click to upload</span> video file
                        </p>
                        <p className="text-xs text-gray-500">MP4, WebM, or OGG (MAX. 100MB)</p>
                      </div>
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => setFormData(prev => ({ ...prev, video_file: e.target.files[0] }))}
                        className="hidden"
                        {...(!editingVideo && { required: true })}
                      />
                    </label>
                  </div>
                  {formData.video_file && (
                    <p className="text-sm text-gray-600 mt-2">
                      Selected: {formData.video_file.name}
                    </p>
                  )}
                  {editingVideo && (
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty to keep current video file
                    </p>
                  )}
                </div>

                {/* Order Index */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order
                  </label>
                  <input
                    type="number"
                    value={formData.order_index}
                    onChange={(e) => setFormData(prev => ({ ...prev, order_index: parseInt(e.target.value) || 0 }))}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaSave size={14} />
                    {editingVideo ? 'Update' : 'Add'} Video
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoManager;